/**
 * "Doanh thu theo tháng" on the home page (reference screen 01): this month's revenue against
 * last month, and the last 12 months as columns. Revenue is what the ledger recognised (before
 * tax, net of refunds) in VND per Vietnamese calendar month; other currencies are named, never
 * converted. Hover or focus a column for its amount; the table view lists every month.
 */

import { ArrowDownRight, ArrowRight, ArrowUpRight, BarChart3, Table2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { api } from '../../api';
import type { MonthlyRevenue } from '../../api/areas/counts';
import { DataTable, Section } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { Loading } from '../../components/States';
import { currentLocale } from '../../i18n';
import { formatMoney } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { cn } from '../../lib/utils';
import { hubUrl } from '../../nav/hubs';

const H = 220;
const PAD = { top: 20, right: 8, bottom: 26, left: 64 };
const BAR_MAX = 24;

/** "2026-09" → "09/2026". */
const monthLabel = (m: string) => `${m.slice(5, 7)}/${m.slice(0, 4)}`;

/** Clean axis steps from 0 (the columns grow from one baseline). */
function ticks(max: number): number[] {
  if (max <= 0) return [0, 1];
  const raw = max / 4;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? raw;
  const out = [0];
  while ((out[out.length - 1] ?? 0) < max) out.push(Number(((out.length) * step).toPrecision(12)));
  return out;
}

export function HomeRevenue() {
  const { t } = useTranslation('ux');
  const [table, setTable] = useState(false);
  const r = useApi((signal) => api.getMonthlyRevenue({ months: 12 }, { signal }), []);

  return (
    <Section
      className="mb-5"
      title={t('revenue.title')}
      description={t('revenue.description')}
      actions={
        <>
          <Button type="button" variant="ghost" size="sm" onClick={() => setTable(!table)} aria-pressed={table}>
            {table ? <BarChart3 /> : <Table2 />} {table ? t('revenue.showChart') : t('revenue.showTable')}
          </Button>
          <Button asChild variant="outline" size="sm">
            <Link to={hubUrl('sales', 'ledger')}>
              {t('revenue.openLedger')} <ArrowRight />
            </Link>
          </Button>
        </>
      }
    >
      <div data-home-revenue>
        {r.status === 'loading' ? <Loading rows={3} /> : null}
        {r.status === 'error' ? <DataError error={r.error} onRetry={r.reload} title={t('revenue.loadError')} /> : null}
        {r.status === 'success' ? <Body data={r.data} table={table} /> : null}
      </div>
    </Section>
  );
}

function Body({ data, table }: { data: MonthlyRevenue; table: boolean }) {
  const { t } = useTranslation('ux');
  const money = (amount: string) => formatMoney({ amount, currency: data.currency });
  const cur = data.months[data.months.length - 1];
  const prev = data.months[data.months.length - 2];
  // Display only: the ratio of two amounts, never money itself.
  const a = Number(cur?.revenue ?? 0);
  const b = Number(prev?.revenue ?? 0);
  const change = b > 0 ? (a - b) / b : null;
  const pct = change === null ? '' : new Intl.NumberFormat(currentLocale(), { style: 'percent', maximumFractionDigits: 1, signDisplay: 'exceptZero' }).format(change);
  const Trend = change === null || change === 0 ? null : change > 0 ? ArrowUpRight : ArrowDownRight;

  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(12rem,16rem)_minmax(0,1fr)]">
      <div className="min-w-0 space-y-1.5">
        <div className="text-[13px] text-muted-foreground">{t('revenue.thisMonth', { month: cur ? monthLabel(cur.month) : '' })}</div>
        <div className="text-[28px] leading-tight font-semibold tracking-tight break-words" data-revenue-current>
          {cur ? money(cur.revenue) : '—'}
        </div>
        <div className={cn('flex items-center gap-1 text-xs', change === null ? 'text-muted-foreground' : change >= 0 ? 'text-success' : 'text-critical')}>
          {Trend ? <Trend className="size-3.5" aria-hidden="true" /> : null}
          {change === null ? t('revenue.noPrevious') : t('revenue.vsLast', { pct })}
        </div>
        {data.other_currencies.length ? <p className="pt-2 text-xs text-muted-foreground">{t('revenue.others', { list: data.other_currencies.join(', ') })}</p> : null}
      </div>
      {table ? (
        <DataTable
          className="rounded-md border"
          columns={[
            { key: 'month', header: t('revenue.month'), cell: (m) => monthLabel(m.month) },
            { key: 'revenue', header: t('revenue.amount'), numeric: true, cell: (m) => money(m.revenue) },
          ]}
          rows={[...data.months].reverse()}
          rowKey={(m) => m.month}
        />
      ) : (
        <Columns data={data} money={money} />
      )}
    </div>
  );
}

function Columns({ data, money }: { data: MonthlyRevenue; money: (amount: string) => string }) {
  const { t } = useTranslation('ux');
  const box = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(640);
  const [hover, setHover] = useState<number | null>(null);
  useEffect(() => {
    const el = box.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([e]) => {
      const w = Math.round(e?.contentRect.width ?? 0);
      if (w > 0) setW(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Geometry only (pixels), from the decimal amounts; negative months (refunds larger than sales) sit at 0.
  const values = data.months.map((m) => Math.max(0, Number(m.revenue)));
  const axis = ticks(Math.max(...values, 0));
  const top = axis[axis.length - 1] ?? 1;
  const compact = new Intl.NumberFormat(currentLocale(), { notation: 'compact', maximumFractionDigits: 1 });
  const plotW = Math.max(1, W - PAD.left - PAD.right);
  const band = plotW / Math.max(1, data.months.length);
  const barW = Math.min(BAR_MAX, Math.max(4, band - 6));
  const y = (v: number) => H - PAD.bottom - (v / top) * (H - PAD.top - PAD.bottom);
  const last = data.months.length - 1;
  // Every other month label on narrow screens.
  const every = band < 34 ? 2 : 1;
  const hovered = hover !== null ? data.months[hover] : undefined;

  const column = (v: number, x: number) => {
    const h = Math.max(0, H - PAD.bottom - y(v));
    if (h <= 0) return '';
    const r = Math.min(4, h, barW / 2);
    const x0 = x - barW / 2;
    const yb = H - PAD.bottom;
    const yt = yb - h;
    // Rounded data end, square at the baseline.
    return `M${x0},${yb}V${yt + r}Q${x0},${yt} ${x0 + r},${yt}H${x0 + barW - r}Q${x0 + barW},${yt} ${x0 + barW},${yt + r}V${yb}Z`;
  };

  return (
    <figure className="m-0 min-w-0">
      <figcaption className="sr-only">{t('revenue.title')}</figcaption>
      <div className="relative" ref={box}>
        <svg viewBox={`0 0 ${W} ${H}`} className="block w-full overflow-visible" style={{ height: H }} role="img" aria-label={t('revenue.chartLabel')}>
          {axis.map((v) => (
            <g key={v}>
              <line x1={PAD.left} x2={W - PAD.right} y1={y(v)} y2={y(v)} className="stroke-border" strokeWidth={1} />
              <text x={PAD.left - 8} y={y(v) + 4} textAnchor="end" className="tabular fill-muted-foreground text-[11px]">
                {compact.format(v)}
              </text>
            </g>
          ))}
          {data.months.map((m, i) => {
            const cx = PAD.left + band * (i + 0.5);
            const v = values[i] ?? 0;
            return (
              <g key={m.month}>
                <path d={column(v, cx)} style={{ fill: 'var(--chart-1)' }} opacity={hover === null || hover === i ? 1 : 0.55} />
                {i === last && v > 0 ? (
                  <text x={cx} y={y(v) - 6} textAnchor={band * (last + 0.5) + PAD.left > W - 40 ? 'end' : 'middle'} className="fill-foreground text-[11px] font-medium">
                    {compact.format(v)}
                  </text>
                ) : null}
                {(last - i) % every === 0 ? (
                  <text x={cx} y={H - 8} textAnchor="middle" className="tabular fill-muted-foreground text-[11px]">
                    {`${m.month.slice(5, 7)}/${m.month.slice(2, 4)}`}
                  </text>
                ) : null}
                {/* The whole band is the hover / focus target, not just the column. */}
                <rect
                  x={cx - band / 2}
                  y={PAD.top}
                  width={band}
                  height={H - PAD.top - PAD.bottom}
                  fill="transparent"
                  tabIndex={0}
                  aria-label={`${monthLabel(m.month)}: ${money(m.revenue)}`}
                  className="outline-none focus-visible:stroke-ring"
                  onPointerEnter={() => setHover(i)}
                  onPointerLeave={() => setHover(null)}
                  onFocus={() => setHover(i)}
                  onBlur={() => setHover(null)}
                />
              </g>
            );
          })}
        </svg>
        {hovered && hover !== null ? (
          <div
            role="status"
            className={cn(
              'pointer-events-none absolute top-0 z-10 rounded-md border bg-popover px-2.5 py-1.5 text-xs text-popover-foreground shadow-md',
              PAD.left + band * (hover + 0.5) > W * 0.6 ? '-translate-x-full -ml-2' : 'ml-2',
            )}
            style={{ left: PAD.left + band * (hover + 0.5) }}
          >
            <div className="text-muted-foreground">{monthLabel(hovered.month)}</div>
            <div className="tabular font-semibold">{money(hovered.revenue)}</div>
          </div>
        ) : null}
      </div>
    </figure>
  );
}
